"use client";
import { logger } from "@/utils/logger";

import { useState, useRef, useEffect } from "react";
import { FloatingInput, FloatingTextarea } from "@/components/ui/FloatingInput";
import { MapPin, Mail, Phone, ArrowRight, ShieldCheck, CheckCircle2, MessageSquare } from "lucide-react";
import Footer from "@/components/layout/Footer";
import { useSettings } from "@/context/SettingsContext";
import { useQuoteModal } from "@/context/QuoteModalContext";

export default function ContactPage() {
  const settings = useSettings();
  const { openModal } = useQuoteModal();
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const bg1Ref = useRef<HTMLDivElement>(null);
  const bg2Ref = useRef<HTMLDivElement>(null);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const dashRingRef = useRef<HTMLDivElement>(null);
  const checkIconRef = useRef<HTMLDivElement>(null);
  const formElementsRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const emailParam = params.get('email');
      if (emailParam) {
        setFormState(prev => ({ ...prev, email: emailParam }));
      }
    }

    import("gsap").then(({ default: gsap }) => {
      // Rotating backgrounds
      if (bg1Ref.current) gsap.to(bg1Ref.current, { rotation: 360, duration: 150, repeat: -1, ease: "linear" });
      if (bg2Ref.current) gsap.to(bg2Ref.current, { rotation: -360, duration: 200, repeat: -1, ease: "linear" });

      // Initial load animations
      gsap.fromTo(".contact-stagger",
        { y: 40, opacity: 0, scale: 0.95 },
        { y: 0, opacity: 1, scale: 1, duration: 0.8, stagger: 0.15, delay: 0.2, ease: "elastic.out(1, 0.7)" }
      );

      if (formContainerRef.current) {
        gsap.fromTo(formContainerRef.current,
          { opacity: 0, x: 100 },
          { opacity: 1, x: 0, duration: 0.8, delay: 0.4, ease: "power3.out" }
        );
      }
    });
  }, []);

  useEffect(() => {
    import("gsap").then(({ default: gsap }) => {
      if (submitted) {
        if (successRef.current) {
          gsap.fromTo(successRef.current, { opacity: 0, scale: 0.9, y: 20 }, { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: "power2.out" });
        }
        if (checkIconRef.current) {
          gsap.fromTo(checkIconRef.current, { scale: 0 }, { scale: 1, duration: 0.6, delay: 0.2, ease: "elastic.out(1, 0.5)" });
        }
        if (dashRingRef.current) {
          gsap.to(dashRingRef.current, { rotation: 360, duration: 10, repeat: -1, ease: "linear" });
        }
        gsap.fromTo(".success-stagger", { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, stagger: 0.1, delay: 0.3 });
      } else {
        if (formElementsRef.current) {
          gsap.fromTo(formElementsRef.current, { opacity: 0 }, { opacity: 1, duration: 0.4 });
        }
      }
    });
  }, [submitted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { [key: string]: string } = {};

    if (!formState.name.trim()) newErrors.name = "Please provide your full name";

    if (!formState.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formState.subject.trim()) newErrors.subject = "Subject is required";
    if (!formState.message.trim()) newErrors.message = "Please enter a message";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      setErrors({});
      setIsSubmitting(true);
      try {
        const res = await fetch('/api/public/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formState.name,
            email: formState.email,
            phone: formState.phone,
            subject: formState.subject,
            message: formState.message,
          })
        });
        if (res.ok) {
          setSubmitted(true);
          setFormState({ name: "", email: "", phone: "", subject: "", message: "" });
        } else {
          logger.error("Submission failed");
        }
      } catch (err) {
        logger.error(err);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-brand-white font-sans overflow-x-hidden pt-32 lg:pt-48">

      {/* 1. HERO SECTION (Creative, Highly Animated) */}
      <section className="relative w-full min-h-[90vh] flex flex-col lg:flex-row px-6 md:px-12 lg:px-24 py-12 gap-16 lg:gap-8 max-w-[1600px] mx-auto overflow-hidden">

        {/* Decorative Abstract Background Elements */}
        <div
          ref={bg1Ref}
          className="absolute -top-[20%] -left-[10%] w-[800px] h-[800px] bg-[radial-gradient(circle,_#0d9488_0%,_transparent_60%)] opacity-[0.03] pointer-events-none rounded-full"
        />
        <div
          ref={bg2Ref}
          className="absolute -bottom-[20%] -right-[10%] w-[1000px] h-[1000px] bg-[radial-gradient(circle,_#3b82f6_0%,_transparent_60%)] opacity-[0.03] pointer-events-none rounded-full"
        />

        {/* Left Side: Contact Information */}
        <div
          className="w-full lg:w-5/12 flex flex-col justify-center relative z-10"
        >
          <div className="contact-stagger opacity-0 flex items-center gap-3 mb-6">
            <div className="w-12 h-[2px] bg-[#0d9488]" />
            <span className="text-[#0d9488] tracking-[0.2em] uppercase text-[12px] font-bold">Get in Touch</span>
          </div>

          <h1 className="contact-stagger opacity-0 text-[48px] md:text-[64px] font-extrabold text-[#0f172a] leading-[1.1] mb-8 tracking-tight font-serif">
            Contact <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0d9488] to-[#2dd4bf]">us</span>
          </h1>

          <p className="contact-stagger opacity-0 text-[17px] text-gray-600 leading-relaxed mb-12 max-w-md border-l-4 border-teal-100 pl-5 italic">
            {settings.contactUsInformation.split(/(<a\s+[^>]*>.*?<\/a>)/gi).map((part, index) => {
              if (part.toLowerCase().startsWith('<a ')) {
                const match = part.match(/<a\s+[^>]*>(.*?)<\/a>/i);
                const text = match ? match[1] : part;

                const quoteMeMatch = text.match(/^(.*?)(Quote\s*Me!?)(.*)$/i);
                if (quoteMeMatch) {
                  return (
                    <span key={index}>
                      {quoteMeMatch[1]}
                      <button
                        onClick={openModal}
                        type="button"
                        className="text-[#0d9488] font-bold hover:underline transition-colors cursor-pointer inline bg-transparent p-0 border-none"
                      >
                        {quoteMeMatch[2]}
                      </button>
                      {quoteMeMatch[3]}
                    </span>
                  );
                }

                return (
                  <button
                    key={index}
                    onClick={openModal}
                    type="button"
                    className="text-[#0d9488] font-bold hover:underline transition-colors cursor-pointer inline bg-transparent p-0 border-none"
                  >
                    {text}
                  </button>
                );
              }
              return <span key={index}>{part}</span>;
            })}
          </p>

          <div className="flex flex-col gap-8">
            <div className="contact-stagger opacity-0 flex gap-5 items-start group">
              <div className="w-14 h-14 rounded-[16px] bg-white shadow-[0_8px_30px_rgba(13,148,136,0.12)] flex items-center justify-center shrink-0 border border-gray-50 group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-300">
                <MapPin className="w-6 h-6 text-[#0d9488]" />
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-bold uppercase tracking-widest text-gray-400 mb-1">Address</span>
                <a href={`https://maps.google.com/?q=${encodeURIComponent(settings.address || '')}`} target="_blank" rel="noopener noreferrer" className="text-[16px] text-gray-800 hover:text-[#0d9488] font-medium leading-relaxed whitespace-pre-line transition-colors">
                  {settings.address}
                </a>
              </div>
            </div>

            <div className="contact-stagger opacity-0 flex gap-5 items-start group">
              <div className="w-14 h-14 rounded-[16px] bg-white shadow-[0_8px_30px_rgba(13,148,136,0.12)] flex items-center justify-center shrink-0 border border-gray-50 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                <Mail className="w-6 h-6 text-[#0d9488]" />
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-bold uppercase tracking-widest text-gray-400 mb-1">Email</span>
                <a href={`mailto:${settings.contactEmail}`} className="text-[16px] text-[#0d9488] hover:text-[#0f766e] font-bold transition-colors">
                  {settings.contactEmail}
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* Right Side: The Interactive Form (Glassmorphism + Animations) */}
        <div
          ref={formContainerRef}
          className="w-full lg:w-7/12 flex items-center relative z-10 opacity-0"
        >
          <div className="w-full bg-white rounded-[40px] shadow-[0_30px_80px_rgba(13,148,136,0.08)] p-8 md:p-12 relative overflow-hidden border border-white">

            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-50 rounded-full blur-[80px] -z-10 translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-50 rounded-full blur-[60px] -z-10 -translate-x-1/2 translate-y-1/2" />

            <h3 className="text-[24px] font-bold text-gray-900 mb-8 flex items-center gap-3">
              <MessageSquare className="w-6 h-6 text-[#0d9488]" />
              Contact Form
            </h3>

            {submitted ? (
              <div
                ref={successRef}
                className="flex flex-col items-center justify-center text-center py-16 opacity-0"
              >
                <div
                  ref={checkIconRef}
                  className="w-24 h-24 bg-gradient-to-tr from-teal-50 to-teal-100 rounded-full flex items-center justify-center mb-8 shadow-[0_10px_40px_rgba(13,148,136,0.15)] relative scale-0"
                >
                  <div
                    ref={dashRingRef}
                    className="absolute inset-0 rounded-full border border-dashed border-teal-300"
                  />
                  <CheckCircle2 className="w-12 h-12 text-[#0d9488]" strokeWidth={1.5} />
                </div>
                <h4
                  className="success-stagger opacity-0 text-[28px] md:text-[32px] font-extrabold text-gray-900 mb-4 font-serif"
                >
                  Thank You!
                </h4>
                <p
                  className="success-stagger opacity-0 text-gray-500 text-[16px] max-w-md mx-auto leading-relaxed mb-10"
                >
                  Your message has been successfully sent. Our team will review your inquiry and connect with you shortly.
                </p>

                <button
                  onClick={() => setSubmitted(false)}
                  className="success-stagger opacity-0 text-[14px] font-bold text-white bg-[#0a2540] hover:bg-[#0d9488] px-8 py-3.5 rounded-full transition-colors shadow-lg hover:shadow-teal-900/20"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                ref={formElementsRef}
                onSubmit={handleSubmit}
                className="flex flex-col gap-6 opacity-0"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-bold text-gray-700">Please enter your name(<span className="text-red-500">*</span>)</label>
                    <input
                      type="text"
                      value={formState.name}
                      onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                      className={`w-full bg-gray-50 border ${errors.name ? 'border-red-400 focus:border-red-500 bg-red-50/30' : 'border-gray-200 focus:border-[#0d9488]'} rounded-[16px] px-5 py-4 text-[15px] outline-none transition-all duration-300 focus:bg-white`}
                      placeholder="Your name"
                    />
                    {errors.name && <span className="text-[12px] text-red-500 font-medium transition-opacity">{errors.name}</span>}
                  </div>

                  {/* Email */}
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-bold text-gray-700">Your email address(<span className="text-red-500">*</span>)</label>
                    <input
                      type="email"
                      value={formState.email}
                      onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                      className={`w-full bg-gray-50 border ${errors.email ? 'border-red-400 focus:border-red-500 bg-red-50/30' : 'border-gray-200 focus:border-[#0d9488]'} rounded-[16px] px-5 py-4 text-[15px] outline-none transition-all duration-300 focus:bg-white`}
                      placeholder="Your email address"
                    />
                    {errors.email && <span className="text-[12px] text-red-500 font-medium transition-opacity">{errors.email}</span>}
                  </div>

                  {/* Phone */}
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-bold text-gray-700">Contact Telephone No:</label>
                    <input
                      type="tel"
                      value={formState.phone}
                      onChange={(e) => setFormState({ ...formState, phone: e.target.value })}
                      className={`w-full bg-gray-50 border border-gray-200 focus:border-[#0d9488] rounded-[16px] px-5 py-4 text-[15px] outline-none transition-all duration-300 focus:bg-white`}
                      placeholder="Your phone number"
                    />
                  </div>

                  {/* Subject */}
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-bold text-gray-700">Message Subject(<span className="text-red-500">*</span>)</label>
                    <input
                      type="text"
                      value={formState.subject}
                      onChange={(e) => setFormState({ ...formState, subject: e.target.value })}
                      className={`w-full bg-gray-50 border ${errors.subject ? 'border-red-400 focus:border-red-500 bg-red-50/30' : 'border-gray-200 focus:border-[#0d9488]'} rounded-[16px] px-5 py-4 text-[15px] outline-none transition-all duration-300 focus:bg-white`}
                      placeholder="Subject"
                    />
                    {errors.subject && <span className="text-[12px] text-red-500 font-medium transition-opacity">{errors.subject}</span>}
                  </div>
                </div>

                {/* Message */}
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-bold text-gray-700">Please enter your message</label>
                  <textarea
                    value={formState.message}
                    onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                    rows={5}
                    className={`w-full bg-gray-50 border ${errors.message ? 'border-red-400 focus:border-red-500 bg-red-50/30' : 'border-gray-200 focus:border-[#0d9488]'} rounded-[16px] px-5 py-4 text-[15px] outline-none transition-all duration-300 focus:bg-white resize-none`}
                    placeholder="Message"
                  />
                  {errors.message && <span className="text-[12px] text-red-500 font-medium transition-opacity">{errors.message}</span>}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full md:w-auto self-end mt-4 bg-gray-900 hover:bg-[#0d9488] text-white font-bold text-[15px] px-12 py-4 rounded-[16px] transition-all duration-300 flex items-center justify-center gap-3 shadow-[0_10px_30px_rgba(13,148,136,0.2)] hover:shadow-[0_15px_40px_rgba(13,148,136,0.3)] hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
                >
                  {isSubmitting ? "Submitting..." : "Submit"} {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                </button>
              </form>
            )}
          </div>
        </div>

      </section>

      {/* 2. INFINITE MARQUEE CTA BAR */}
      <div className="w-full bg-[#0f172a] text-white py-5 overflow-hidden flex items-center z-20 mt-16 group border-y border-gray-800">
        {[0, 1].map((containerIdx) => (
          <div key={containerIdx} className="flex whitespace-nowrap animate-marquee group-hover:[animation-play-state:paused]" aria-hidden={containerIdx === 1 ? "true" : "false"}>
            {[...Array(3)].map((_, i) => (
              <span key={i} className="text-[13px] md:text-[14px] font-bold tracking-[0.1em] uppercase flex items-center pr-12">
                To find out more or for a free, no obligation consultation please {settings.contactPhone?.trim() ? <>call <a href={`tel:${settings.contactPhone}`} className="text-[#2dd4bf] hover:text-[#14b8a6] hover:underline transition-colors mx-2">{settings.contactPhone}</a> or email us at</> : "email us at"} <a href={`mailto:${settings.contactEmail}`} className="text-[#2dd4bf] hover:text-[#14b8a6] hover:underline transition-colors ml-2">{settings.contactEmail}</a>
                <span className="w-1.5 h-1.5 rounded-full bg-white opacity-20 ml-12" />
              </span>
            ))}
          </div>
        ))}
      </div>

    </div>
  );
}
